import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FileText, Calendar, Zap, ArrowRight, CheckCircle, Shield,
} from "react-feather";
import { Navbar } from "./Navbar";
import { FEATURES } from "./Home";

const api = process.env.REACT_APP_API_URL;

/* The MEMBER home, what a signed-in user lands on. Same design language as
   the public landing, but it works for a living: live figures from the user's
   own account and direct launchers into the three product areas. */

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

function Home1() {
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);   // {jobs, submitted, confirmed, credits}

  useEffect(() => {
    window.scrollTo(0, 0);
    localStorage.removeItem("completedSteps");
  }, []);

  // Live account snapshot, best-effort; the page reads fine without it.
  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      axios.get(`${api}autoapply/jobs/?limit=1`, { withCredentials: true }),
      axios.get(`${api}autoapply/applications/`, { withCredentials: true }),
      axios.get(`${api}autoapply/balance/`, { withCredentials: true }),
    ]).then(([j, a, b]) => {
      if (!alive) return;
      const apps = a.status === "fulfilled" ? (a.value.data.applications || []) : [];
      setSnapshot({
        jobs: j.status === "fulfilled" ? j.value.data.total : null,
        submitted: apps.filter((x) => ["pressed", "confirmed"].includes(x.status)).length,
        confirmed: apps.filter((x) => x.status === "confirmed").length,
        credits: b.status === "fulfilled" ? b.value.data.balance : null,
      });
    });
    return () => { alive = false; };
  }, []);

  const CARDS = [
    {
      icon: FileText,
      title: "Resume Studio",
      desc: "Upload, score against a JD, tailor with AI, and export from 4 recruiter-grade templates.",
      cta: "Build / update resume",
      go: () => navigate("/upload"),
      accent: "from-blue-600 to-blue-500",
    },
    {
      icon: Zap,
      title: "Auto-Apply",
      desc: "Your matched job feed, one-click assisted applications, and a tracker with real employer confirmations.",
      cta: "Open Auto-Apply",
      go: () => navigate("/auto-apply"),
      accent: "from-sky-500 to-blue-600",
      chips: snapshot && [
        snapshot.jobs != null && `${Number(snapshot.jobs).toLocaleString()} live jobs`,
        `${snapshot.submitted} submitted`,
        snapshot.confirmed > 0 && `${snapshot.confirmed} confirmed`,
        snapshot.credits != null && `${Number(snapshot.credits).toLocaleString()} credits`,
      ].filter(Boolean),
    },
    {
      icon: Calendar,
      title: "Career Coaching",
      desc: "Book a one-on-one session with an advisor for resume review, interview practice, and strategy.",
      cta: "Book a session",
      go: () => navigate("/appointment"),
      accent: "from-indigo-500 to-blue-600",
    },
  ];

  return (
    <>
      <Navbar />
      <div className="bg-white">

        {/* ── Hero, welcome back, with the state of your search ───────── */}
        <div className="relative overflow-hidden bg-gradient-to-b from-sky-50 via-blue-50/50 to-white">
          <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-blue-200/40 blur-3xl" />
          <div className="pointer-events-none absolute top-40 -left-32 w-80 h-80 rounded-full bg-sky-200/40 blur-3xl" />

          <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-14 pb-16 text-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-100/80 border border-blue-200 px-3 py-1 rounded-full">
                <Shield className="w-3 h-3" /> Signed in. Your job search is running.
              </span>
              <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold text-gray-900 leading-[1.1] tracking-tight">
                Welcome back.
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-sky-500">
                  Let's get you hired.
                </span>
              </h1>

              {/* Live account figures */}
              {snapshot && (
                <div className="mt-6 flex flex-wrap items-center justify-center gap-6 sm:gap-10">
                  {[
                    snapshot.jobs != null && [Number(snapshot.jobs).toLocaleString(), "live jobs matched to you"],
                    [snapshot.submitted, "applications submitted"],
                    [snapshot.confirmed, "confirmed by employers"],
                    snapshot.credits != null && [Number(snapshot.credits).toLocaleString(), "credits ready"],
                  ].filter(Boolean).map(([n, l]) => (
                    <div key={l} className="text-center">
                      <p className="text-3xl font-extrabold text-gray-900">{n}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{l}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={() => navigate("/auto-apply")}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white font-bold px-6 py-3 rounded-xl hover:bg-blue-500 shadow-lg shadow-blue-600/20 transition"
                >
                  <Zap className="w-4 h-4" /> Open Auto-Apply
                </button>
                <button
                  onClick={() => navigate("/upload")}
                  className="inline-flex items-center gap-2 bg-white text-gray-800 font-semibold px-6 py-3 rounded-xl border border-gray-300 hover:border-blue-400 hover:text-blue-700 transition"
                >
                  <FileText className="w-4 h-4" /> Build / update my resume
                </button>
              </div>
              <button
                onClick={() => navigate("/express")}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 hover:text-amber-500 transition"
              >
                ⚡ In a hurry? Lightning Fast resume, done in minutes
              </button>
            </motion.div>
          </div>
        </div>

        {/* ── The three product areas ──────────────────────────────────── */}
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-14">
          <div className="grid md:grid-cols-3 gap-5">
            {CARDS.map(({ icon: Icon, title, desc, cta, go, accent, chips }, i) => (
              <motion.button
                key={title}
                onClick={go}
                className="group text-left bg-white border border-gray-200 rounded-2xl p-6 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-600/5 transition flex flex-col"
                {...fadeUp}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <span className={`inline-flex w-11 h-11 rounded-xl bg-gradient-to-br ${accent} text-white items-center justify-center shadow-md`}>
                  <Icon className="w-5 h-5" />
                </span>
                <h3 className="mt-4 text-lg font-bold text-gray-900">{title}</h3>
                <p className="mt-1.5 text-sm text-gray-600 leading-relaxed flex-1">{desc}</p>
                {chips && chips.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {chips.map((c) => (
                      <span key={c} className="text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full">{c}</span>
                    ))}
                  </div>
                )}
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 group-hover:gap-2.5 transition-all">
                  {cta} <ArrowRight className="w-4 h-4" />
                </span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* ── Everything you have, the full feature set ───────────────── */}
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pb-16">
          <motion.div className="text-center max-w-2xl mx-auto mb-10" {...fadeUp}>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
              Everything included in your account
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                className="group bg-white border border-gray-200 rounded-2xl p-6 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-600/5 transition"
                {...fadeUp}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              >
                <span className="inline-flex w-10 h-10 rounded-xl bg-blue-50 text-blue-600 items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition">
                  <Icon className="w-5 h-5" />
                </span>
                <h3 className="mt-4 font-bold text-gray-900">{title}</h3>
                <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ── Final nudge ──────────────────────────────────────────────── */}
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pb-20">
          <motion.div className="bg-gradient-to-r from-blue-600 to-sky-500 rounded-3xl px-8 py-10 text-center text-white shadow-xl shadow-blue-600/20" {...fadeUp}>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              The best time to apply was this morning. The second best is now.
            </h2>
            <p className="mt-2 text-blue-100 text-sm">
              <CheckCircle className="w-4 h-4 inline mr-1 -mt-0.5" />
              Your feed is ranked, your resume is ready, and the agent does the typing, you just press Submit.
            </p>
            <button
              onClick={() => navigate("/auto-apply")}
              className="mt-6 inline-flex items-center gap-2 bg-white text-blue-700 font-bold px-7 py-3 rounded-xl hover:bg-blue-50 transition"
            >
              <Zap className="w-4 h-4" /> Apply to today's matches
            </button>
          </motion.div>
        </div>
      </div>
    </>
  );
}

export default Home1;
